import { ComponentType, ReactNode, ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { twMerge } from "tailwind-merge";

type IconComponent = ComponentType<{
  className?: string;
  strokeWidth?: number;
}>;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  icon?: IconComponent;
  loading?: boolean;
  children?: ReactNode;
  className?: string;
}

const disabledClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-content-disabled text-white",
  secondary: "bg-content-disabled text-white",
  outline: "text-content-tertiary border-transparent",
};

const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary:
    "bg-[#111827] text-white hover:bg-[#1F2937] transition-colors duration-150",
  secondary:
    "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB] transition-colors duration-150",
  outline:
    "bg-white text-status-neutral-content hover:bg-[#E5E7EB] transition-colors duration-150 border-1",
};

const sizeClasses: Record<
  NonNullable<ButtonProps["size"]>,
  { root: string; iconOnly: string; iconSize: string; text: string }
> = {
  sm: {
    root: "h-6 rounded-[5px]",
    iconOnly: "w-6",
    iconSize: "size-3",
    text: "px-2 gap-1 text-[11px] font-medium",
  },
  md: {
    root: "h-7 rounded-[6px]",
    iconOnly: "w-7",
    iconSize: "size-3.5",
    text: "px-2.5 gap-1.5 text-[12px] font-medium",
  },
  lg: {
    root: "rounded-[12px]",
    iconOnly: "size-8",
    iconSize: "size-[15px]",
    text: "p-3 gap-2 font-semibold text-[13px]",
  },
};

export function Button({
  variant = "secondary",
  size = "md",
  icon: Icon,
  loading,
  children,
  className,
  ...props
}: ButtonProps) {
  const isIconOnly = Icon && !children;
  const s = sizeClasses[size];

  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={twMerge(
        "flex shrink-0 cursor-pointer items-center justify-center border-0 outline-none",
        variantClasses[variant],
        s.root,
        isIconOnly ? s.iconOnly : s.text,
        className,
        (props.disabled || loading) &&
          `pointer-events-none ${disabledClasses[variant]}`,
      )}
    >
      {loading ? (
        <Loader2 className={twMerge(s.iconSize, "animate-spin")} />
      ) : (
        Icon && <Icon className={s.iconSize} strokeWidth={2.5} />
      )}
      {children}
    </button>
  );
}
